import _ from "lodash";
import React, { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { DndContext, closestCenter, MeasuringStrategy } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  defaultAnimateLayoutChanges,
} from "@dnd-kit/sortable";
import {
  restrictToParentElement,
  restrictToHorizontalAxis,
} from "@dnd-kit/modifiers";
import { colors } from "@toddle-edu/ds-theme";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import classes from "./ReorderAttachment.scss";

import MediaPlaceholder from "UIComponents/MediaPlaceholder";
import className from "classnames";
import { generateRandomId, onEnterOrSpaceKeyDown } from "Utils";

const styles = {
  editAttachmentModalBody: {
    display: "flex",
    gap: "16px",
  },
  customDeleteButtonStyle: {
    top: "12px",
  },
};

const RenderAttachment = ({
  id,
  isActive,
  mediaIndex,
  animateLayoutChanges,
  media,
  attachmentSize,
  toggleMediaModalWithIndex,
  toggleEditAttachmentModal,
  handleDelete,
  reorderTooltip,
  isLoading,
  isActivityTrackingPost = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    data: { index: mediaIndex },
    animateLayoutChanges,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const onClickMedia = () => {
    if (isDragging) return;
    toggleMediaModalWithIndex({
      value: true,
      attachmentIndex: mediaIndex,
    });
  };
  const { t } = useTranslation(["common"]);
  const onHandleKeyDown = e => {
    onEnterOrSpaceKeyDown(e, () => {
      toggleMediaModalWithIndex({
        value: true,
        attachmentIndex: mediaIndex,
      });
    });
  };

  return (
    <div
      ref={setNodeRef}
      className={classes.container}
      {...attributes}
      onClick={onClickMedia}
      style={{
        zIndex: isDragging ? 1000 : "auto",
        position: "relative",
        ...style,
      }}
      onKeyDown={onHandleKeyDown}
    >
      <div
        className={className({
          [classes.cursorGrabbing]: isActive,
        })}
      >
        <MediaPlaceholder
          attachments={[media]}
          showVideoPlayer={false}
          resolutionMutliplier={3}
          imageMaxWidth={280}
          imageHeight={148}
          imageMaxHeight={148}
          showDeleteIconOnTopCorner={true}
          showIconCardsWithExtension={true}
          isAttachmentCreated={true}
          mode={"edit"}
          containerStyle={{
            minHeight: 162,
            maxHeight: 162,
            overflow: "0px",
            width:
              attachmentSize === 2 ? (isActivityTrackingPost ? 288 : 276) : 262,
          }}
          itemStyles={{
            border: `1px solid ${colors.borderSubtle}`,
            borderRadius: "8px",
          }}
          showOverlayIcons={false}
          deleteAttachment={isLoading ? _.noop : handleDelete}
          extensionTagSize={"small"}
          showBorderOnHover={true}
          showExtensionOnCornerByHover={true}
          showMiddleContainerIcons={false}
          deleteButtonVariant={"small"}
          attachmentSize={attachmentSize}
          toggleEditAttachmentModal={toggleEditAttachmentModal}
          isWorkbook={false}
          showDeleteButtonOnCreateMode={true}
          activatorNodeRef={setActivatorNodeRef}
          listeners={listeners}
          reorderTooltip={reorderTooltip}
          customDeleteButtonStyle={styles.customDeleteButtonStyle}
          deleteIconTooltip={t("common:delete")}
          onClickMedia={onClickMedia}
        />
      </div>
    </div>
  );
};
export default function ReorderAttachment({
  attachments,
  setAttachments,
  toggleMediaModalWithIndex,
  toggleEditAttachmentModal,
  updatePost,
  reorderTooltip,
  setToastMsg,
  isLoading,
  shouldFilterAudio = true,
  isActivityTrackingPost = false,
  studentActivityData,
  studentIds,
  selectedStudentForActivity,
  deleteAttachment,
}) {
  const [activeMediaId, setActiveMediaId] = useState(null);
  const handleDragStart = ({ active }) => {
    setActiveMediaId(_.get(active, "id"));
  };

  const handleDragEnd = ({ active, over }) => {
    // only filter audio attachments if shouldFilterAudio is true
    const [audioAttachments, otherAttachments] = shouldFilterAudio
      ? _.partition(attachments, attachment => {
          // Check if the attachment is an audio file based on some condition
          // For example, assuming 'type' property contains MIME type information
          return attachment?.type === "AUDIO";
        })
      : [[], attachments];
    const updatedAttachments = arrayMove(
      otherAttachments,
      _.get(active, "data.current.index"),
      _.get(over, "data.current.index")
    );
    setActiveMediaId(null);
    setAttachments([...updatedAttachments, ...audioAttachments]);
    updatePost({
      attachments: _.map(
        [...updatedAttachments, ...audioAttachments],
        (attachment, index) => {
          return {
            ..._.omit(attachment, "signedUrl"),
            displaySequence: index + 1,
          };
        }
      ),
    });
  };

  const handleDragEndActivityAttachment = ({ active, over }) => {
    const updatedStudentActivities = _.cloneDeep(studentActivityData);

    // Helper function to filter and reorder attachments
    const getFilteredAndReorderedAttachments = (
      attachments,
      activeIndex,
      overIndex
    ) => {
      const [audioAttachments, otherAttachments] = shouldFilterAudio
        ? _.partition(
            attachments,
            attachment => attachment?.value?.type === "AUDIO"
          )
        : [[], attachments];

      const reorderedAttachments = arrayMove(
        otherAttachments,
        activeIndex,
        overIndex
      );

      return [...reorderedAttachments, ...audioAttachments];
    };

    if (selectedStudentForActivity === "ALL") {
      const allAttachments = _.get(
        updatedStudentActivities,
        ["ALL", "attachments"],
        []
      );
      const updatedAttachments = getFilteredAndReorderedAttachments(
        allAttachments,
        _.get(active, "data.current.index"),
        _.get(over, "data.current.index")
      );

      _.setWith(
        updatedStudentActivities,
        ["ALL", "attachments"],
        updatedAttachments,
        Object
      );

      _.forEach(studentIds, studentId => {
        const oldCommonAttachments = _.get(
          studentActivityData,
          ["ALL", "attachments"],
          []
        );
        const studentAttachments = _.get(
          studentActivityData,
          [studentId, "attachments"],
          []
        );

        // Check if student attachments match common attachments
        if (_.size(oldCommonAttachments) !== _.size(studentAttachments)) {
          return;
        }

        const areAttachmentsIdentical = _.every(
          oldCommonAttachments,
          (attachment, index) => {
            const oldUrl = _.get(attachment, "value.url");
            const studentUrl = _.get(studentAttachments, [
              index,
              "value",
              "url",
            ]);
            return oldUrl === studentUrl;
          }
        );

        if (areAttachmentsIdentical) {
          const studentAttachmentsList = _.get(
            updatedStudentActivities,
            [studentId, "attachments"],
            []
          );
          const reorderedStudentAttachments = getFilteredAndReorderedAttachments(
            studentAttachmentsList,
            _.get(active, "data.current.index"),
            _.get(over, "data.current.index")
          );

          _.set(
            updatedStudentActivities,
            [studentId, "attachments"],
            reorderedStudentAttachments
          );
        }
      });
    } else {
      const studentAttachments = _.get(
        updatedStudentActivities,
        [selectedStudentForActivity, "attachments"],
        []
      );
      const updatedAttachments = getFilteredAndReorderedAttachments(
        studentAttachments,
        _.get(active, "data.current.index"),
        _.get(over, "data.current.index")
      );

      _.set(
        updatedStudentActivities,
        [selectedStudentForActivity, "attachments"],
        updatedAttachments
      );
    }

    updatePost({
      studentActivityData: updatedStudentActivities,
    });
  };

  const handleDelete = ({ id }) => {
    const postattachments = [...attachments];
    if (isActivityTrackingPost) {
      const attachmentToDelete = _.find(attachments, { id });
      deleteAttachment(attachmentToDelete, () => {
        setToastMsg({
          msg: "journal:media_delete_message",
          type: "success",
          locale_params: [
            {
              key: "mediaName",
              value: _.get(_.find(attachments, { id }), "name"),
              isPlainText: true,
            },
          ],
        });
      });
    } else {
      setToastMsg({
        msg: "journal:media_delete_message",
        type: "success",
        locale_params: [
          {
            key: "mediaName",
            value: _.get(_.find(attachments, { id }), "name"),
            isPlainText: true,
          },
        ],
      });
      let filteredAttachments = _.filter(
        postattachments,
        option => option.id !== id
      );
      filteredAttachments = _.map(filteredAttachments, (attachment, index) => {
        return { ...attachment, displaySequence: index + 1 };
      });
      setAttachments(filteredAttachments);
      updatePost({ attachments: filteredAttachments });
    }
  };

  const getUpdateFilterAttachments = useMemo(() => {
    let updatedFilterAttachments = _.filter(attachments, attachment => {
      return attachment?.type !== "AUDIO";
    });
    updatedFilterAttachments = _.map(updatedFilterAttachments, attachment => ({
      id: generateRandomId(),
      ...attachment,
    }));
    return updatedFilterAttachments;
  }, [attachments]);

  const animateLayoutChanges = args =>
    defaultAnimateLayoutChanges({ ...args, wasDragging: true });

  const filteredAttachments = shouldFilterAudio
    ? getUpdateFilterAttachments
    : attachments;
  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={
        isActivityTrackingPost ? handleDragEndActivityAttachment : handleDragEnd
      }
      measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      modifiers={[restrictToParentElement, restrictToHorizontalAxis]}
    >
      <div style={styles.editAttachmentModalBody}>
        <SortableContext items={filteredAttachments}>
          {_.map(filteredAttachments, (media, mediaIndex) => (
            <RenderAttachment
              id={media?.id}
              key={media?.id}
              media={media}
              mediaIndex={mediaIndex}
              isActive={media?.id === activeMediaId}
              animateLayoutChanges={animateLayoutChanges}
              handleDelete={handleDelete}
              attachmentSize={_.size(filteredAttachments)}
              toggleMediaModalWithIndex={toggleMediaModalWithIndex}
              toggleEditAttachmentModal={toggleEditAttachmentModal}
              reorderTooltip={reorderTooltip}
              isLoading={isLoading}
              isActivityTrackingPost={isActivityTrackingPost}
            />
          ))}
        </SortableContext>
      </div>
    </DndContext>
  );
}
